import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  HelpOutline,
  CalendarMonth,
  AccessTime,
  Group,
  SwapHoriz,
  BeachAccess,
  Settings,
  Payment,
  Notifications,
  ExpandMore,
  PlayArrow,
  Dashboard,
  Search as SearchIcon,
  SearchOff,
} from '@mui/icons-material';

interface HelpWikiModalProps {
  open: boolean;
  onClose: () => void;
  initialSection?: string;
}

interface WikiSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  role?: 'manager' | 'staff' | 'all';
  searchText: string;
  content: React.ReactNode;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 1 }}>
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" paragraph>
    {children}
  </Typography>
);

const StepList: React.FC<{ steps: string[] }> = ({ steps }) => (
  <Box component="ol" sx={{ pl: 2.5, mt: 0 }}>
    {steps.map((step, i) => (
      <Box component="li" key={i} sx={{ mb: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{step}</Typography>
      </Box>
    ))}
  </Box>
);

const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1, px: 2, py: 1.5, mb: 2 }}>
    <Typography variant="body2" fontWeight={600}>💡 Tip</Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const WIKI_SECTIONS: WikiSection[] = [
  {
    id: 'overview',
    title: 'Getting Started',
    icon: <PlayArrow fontSize="small" />,
    role: 'all',
    searchText: 'getting started welcome Stafflows workforce scheduling quick start create schedule shift position invite join add staff assign setup first',
    content: (
      <Box>
        <SectionHeading>Quick Start Guide</SectionHeading>
        <Para>
          Welcome to Stafflows! This guide walks you through setting up your first schedule, adding
          shifts, inviting your team, and assigning staff — everything you need to get your business
          up and running.
        </Para>

        <SectionHeading>Step 1: Create Your First Schedule</SectionHeading>
        <Para>
          A schedule is the foundation of Stafflows — it defines the time period your shifts repeat on
          (weekly, bi-weekly, or monthly).
        </Para>
        <StepList
          steps={[
            'Click the + button at the top of the left sidebar.',
            'Enter a name for your schedule (e.g., "Main Floor", "Weekend Crew").',
            'Select a frequency — Weekly (7 days), Bi-weekly (14 days), or Monthly.',
            'Click a day on the calendar to set the start date. You\'ll see a preview of the first schedule period and upcoming repeats.',
            'Click Create. Your new schedule will appear in the sidebar.',
          ]}
        />
        <TipBox>
          Choose a start date that aligns with your pay period or work week for the smoothest experience.
        </TipBox>

        <SectionHeading>Step 2: Add Positions</SectionHeading>
        <Para>
          Positions represent the roles in your business (e.g., Cashier, Server, Driver). Setting these
          up first makes it easier to organize shifts and assign staff.
        </Para>
        <StepList
          steps={[
            'Click "Manage Positions" in the Quick Action sidebar on the right.',
            'Click the + button to add a new position.',
            'Enter a name and pick a color for the position.',
            'Repeat for each role you need. You can always add more later.',
          ]}
        />

        <SectionHeading>Step 3: Create Your First Shift</SectionHeading>
        <Para>
          Shifts are the building blocks of your schedule — they define when staff need to work and
          how many people are needed.
        </Para>
        <StepList
          steps={[
            'Click "Add Shift" in the Quick Action sidebar, or click directly on a day cell in the schedule grid.',
            'Enter the shift name, start time, end time, and how many staff are required.',
            'Assign a position to the shift (optional but recommended).',
            'Choose whether the shift is recurring (repeats on selected days each period) or one-time.',
            'Click Save. The shift will appear on the schedule grid.',
          ]}
        />
        <TipBox>
          Recurring shifts save you time — set them once and they automatically appear on the selected
          days for every schedule period.
        </TipBox>

        <SectionHeading>Step 4: Invite Your Team</SectionHeading>
        <Para>
          Now that your schedule and shifts are set up, invite your staff so they can view the schedule
          and get assigned to shifts.
        </Para>
        <StepList
          steps={[
            'Click "Invite Staff" in the Quick Action sidebar.',
            'Enter the employee\'s email address.',
            'Optionally set their access level (Manager or Staff).',
            'Click Send Invite. They\'ll receive an email with a link to join.',
            'Once they accept, they\'ll appear in your schedule\'s staff list.',
          ]}
        />

        <SectionHeading>Step 5: Assign Staff to a Position</SectionHeading>
        <Para>
          Assigning a position to a staff member helps you organize your team and makes it easier to
          fill shifts.
        </Para>
        <StepList
          steps={[
            'Click "Manage Staff" in the Quick Action sidebar.',
            'Find the employee you want to update.',
            'Assign them a position from the dropdown.',
            'Their position will now show on shift cards and the schedule grid.',
          ]}
        />

        <SectionHeading>Step 6: Add Staff to a Shift</SectionHeading>
        <Para>
          The final step — assign your team members to the shifts they'll be working.
        </Para>
        <StepList
          steps={[
            'Click on a shift card in the schedule grid.',
            'In the shift detail view, click the assign/add button.',
            'Select the staff member(s) you want to assign.',
            'Click Save. The shift card will update to show the assigned staff and staffing status.',
          ]}
        />
        <TipBox>
          Shift cards are color-coded: blue means fully staffed, orange means understaffed, and red
          means unstaffed. Use these at a glance to see where you still need coverage.
        </TipBox>

        <SectionHeading>You're All Set!</SectionHeading>
        <Para>
          That's the basics! From here you can explore availability management, shift swapping,
          time-off requests, and more using the other sections in this guide. Your staff will be
          notified automatically when they're assigned to shifts or when the schedule changes.
        </Para>
      </Box>
    ),
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: <Dashboard fontSize="small" />,
    role: 'all',
    searchText: 'dashboard week view month view navigate today schedule grid legend fully staffed understaffed unstaffed unfinalized holiday quick action sidebar shift colors filter',
    content: (
      <Box>
        <SectionHeading>Dashboard Overview</SectionHeading>
        <Para>
          The Dashboard is your main workspace. It shows the schedule grid for the selected
          schedule and provides quick access to all key actions.
        </Para>
        <SectionHeading>Week View vs Month View</SectionHeading>
        <Para>
          Toggle between <strong>Week</strong> and <strong>Month</strong> views using the buttons at the top-left
          of the schedule grid. The week view shows all shifts for each day of the current week. The
          month view gives a broader calendar overview.
        </Para>
        <SectionHeading>Navigating Weeks</SectionHeading>
        <Para>
          Use the left (&lt;) and right (&gt;) arrows beside the date range to move to the previous or next
          week. Click <strong>TODAY</strong> to jump back to the current week.
        </Para>
        <SectionHeading>Schedule Legend</SectionHeading>
        <Para>The colored legend at the bottom of the schedule grid explains what each shift color means:</Para>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 2 }}>
          {[
            { color: '#1976d2', label: 'Fully Staffed' },
            { color: '#ed6c02', label: 'Understaffed' },
            { color: '#d32f2f', label: 'Unstaffed' },
            { color: '#9e9e9e', label: 'Unfinalized Schedule' },
            { color: '#ffccbc', label: 'Holiday' },
            { color: '#ff8a65', label: 'Next Schedule Start' },
            { color: '#1565c0', label: 'Today' },
          ].map(({ color, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>
        <TipBox>
          Click any legend item to toggle that category's visibility on the schedule grid — great for
          focusing on just the shifts you care about.
        </TipBox>
        <SectionHeading>Quick Action Sidebar</SectionHeading>
        <Para>
          The panel on the right side of the dashboard shows your next upcoming shift and a list of
          quick action buttons. These change based on your role — managers see staffing actions while
          staff see availability and request options.
        </Para>
      </Box>
    ),
  },
  {
    id: 'schedules',
    title: 'Schedules',
    icon: <CalendarMonth fontSize="small" />,
    role: 'all',
    searchText: 'schedule create edit delete permissions recurrence weekly biweekly monthly color sidebar three dot menu swap approval pickup staff view availability deadline frequency none repeat period future active submit prepare',
    content: (
      <Box>
        <SectionHeading>What is a Schedule?</SectionHeading>
        <Para>
          A schedule is a named container for shifts, employees, and positions. A business can have
          multiple schedules (e.g., "Front of House", "Kitchen", "Deliveries"). Each schedule is
          independent with its own staff roster, positions, and shift history.
        </Para>
        <SectionHeading>Creating a Schedule</SectionHeading>
        <Para>Managers and admins can create a new schedule:</Para>
        <StepList
          steps={[
            'Click the + button at the top of the left sidebar.',
            'Enter a schedule name and optional color.',
            'Choose a frequency (None, Weekly, Bi-weekly, or Monthly) and, if applicable, set a start date.',
            'Click Create to save.',
          ]}
        />
        <SectionHeading>Schedule Frequency</SectionHeading>
        <Para>
          Frequency controls how often a schedule repeats. When you create a schedule, you choose one of four options:
        </Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'Weekly — The schedule repeats every 7 days.',
            'Bi-weekly — The schedule repeats every 14 days.',
            'Monthly — The schedule repeats every calendar month.',
            'None — The schedule does not repeat. Shifts are created on specific dates with no recurring periods.',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Para>
          When a frequency is set (Weekly, Bi-weekly, or Monthly), the schedule is divided into
          repeating periods. Staff only see the <strong>active period</strong> — the current schedule that is
          in effect. Future periods are visible only to managers, allowing them to collect
          availability, build out shifts, and make adjustments before submitting the schedule for
          staff to see. This gives managers time to prepare each upcoming period without affecting
          what staff see today.
        </Para>
        <Para>
          When frequency is set to <strong>None</strong>, the schedule acts as a shared, always-live
          workspace. Any shifts you create are immediately visible to staff, and employees can
          interact with them right away — picking up open shifts, requesting swaps, and viewing
          assignments without waiting for a manager to submit or finalize anything. This is ideal for
          on-demand or rolling schedules where there is no fixed repeating cycle.
        </Para>
        <TipBox>
          Use a recurring frequency when you want to plan ahead and control when staff see the
          schedule. Use "None" when you need a flexible, real-time schedule where shifts go live
          as soon as they are created.
        </TipBox>
        <SectionHeading>Editing a Schedule</SectionHeading>
        <Para>
          Click the three-dot (⋮) menu next to any schedule in the sidebar and select{' '}
          <strong>Edit</strong> to update its name, color, frequency, or staff permissions.
        </Para>
        <SectionHeading>Schedule Permissions</SectionHeading>
        <Para>Managers can configure per-schedule settings including:</Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'Allow shift swaps without manager approval',
            'Allow shift pickups without approval',
            'Allow staff to view other employees\'s shifts',
            'Availability submission deadline (days before schedule start)',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <SectionHeading>Deleting a Schedule</SectionHeading>
        <Para>
          Use the three-dot menu → <strong>Delete</strong>. This permanently removes the schedule
          and all associated shifts, assignments, and availability data. This action cannot be undone.
        </Para>
      </Box>
    ),
  },
  {
    id: 'shifts',
    title: 'Shifts',
    icon: <AccessTime fontSize="small" />,
    role: 'all',
    searchText: 'shift add create edit delete assign staff recurring one-time start time end time position required count blue orange red fully staffed understaffed unstaffed card schedule grid',
    content: (
      <Box>
        <SectionHeading>What is a Shift?</SectionHeading>
        <Para>
          A shift represents a block of work time on a schedule. Shifts have a name, start/end time,
          position, required staff count, and can be recurring or one-time.
        </Para>
        <SectionHeading>Adding a Shift (Managers)</SectionHeading>
        <StepList
          steps={[
            'Click "Add Shift" in the Quick Action sidebar, or click a day cell on the schedule grid.',
            'Enter the shift name, start time, end time, and required staff count.',
            'Select a position (optional).',
            'Choose whether the shift is recurring (repeats on selected days) or one-time (single date).',
            'Click Save to create the shift.',
          ]}
        />
        <SectionHeading>Shift Status Colors</SectionHeading>
        <Para>
          Each shift card on the schedule grid is color-coded based on how many staff are assigned
          relative to the required count:
        </Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'Blue — Fully staffed (assignments = required)',
            'Orange — Understaffed (at least one but less than required)',
            'Red — Unstaffed (no assignments)',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <SectionHeading>Assigning Staff to Shifts</SectionHeading>
        <Para>
          Click a shift card on the schedule grid to open the shift management panel. From there,
          managers can assign available employees or remove existing assignments.
        </Para>
        <TipBox>
          Staff count on each shift card is displayed as "assigned / required" (e.g., 1/2 means one
          of two required staff is assigned).
        </TipBox>
        <SectionHeading>Editing or Deleting a Shift</SectionHeading>
        <Para>
          Click any shift card and select the edit or delete option from the shift detail panel.
          Editing a recurring shift will update all future occurrences.
        </Para>
      </Box>
    ),
  },
  {
    id: 'availability',
    title: 'Availability',
    icon: <CalendarMonth fontSize="small" />,
    role: 'all',
    searchText: 'availability default weekly available unavailable partial submit override green red yellow orange toggle mode deadline manager staff schedule',
    content: (
      <Box>
        <SectionHeading>Availability Overview</SectionHeading>
        <Para>
          Staff members submit their availability so managers know when they can be scheduled.
          Stafflows supports both <strong>default availability</strong> (recurring weekly pattern)
          and <strong>weekly availability</strong> (overrides for specific weeks).
        </Para>
        <SectionHeading>Default Availability</SectionHeading>
        <Para>
          Your default availability is a recurring weekly pattern that managers use as a baseline.
          Set it once and it applies to all future weeks unless overridden.
        </Para>
        <StepList
          steps={[
            'Click "Update Default Availability" in the Quick Action sidebar.',
            'For each day of the week, mark yourself as Available, Unavailable, or Partially Available.',
            'Add optional time ranges or notes for partial availability.',
            'Click Save to apply.',
          ]}
        />
        <SectionHeading>Weekly Availability</SectionHeading>
        <Para>
          To override your default availability for a specific week, click the{' '}
          <strong>AVAILABILITY</strong> toggle button at the top of the schedule grid to enter
          availability mode. Then click any day to submit or update your availability for that day.
        </Para>
        <TipBox>
          Managers can see an overview of all employee availability directly on the schedule grid
          when availability mode is active, making it easy to staff shifts appropriately.
        </TipBox>
        <SectionHeading>Color Coding in Availability Mode</SectionHeading>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'Green — Available',
            'Red — Unavailable',
            'Yellow/Orange — Partially available',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },
  {
    id: 'requests',
    title: 'Requests & Swaps',
    icon: <SwapHoriz fontSize="small" />,
    role: 'all',
    searchText: 'request time off swap shift pickup approve deny pending manager approval submit date note employee exchange open',
    content: (
      <Box>
        <SectionHeading>Request Types</SectionHeading>
        <Para>Staff members can submit two types of requests:</Para>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {[
            {
              type: 'Time Off Request',
              icon: <BeachAccess fontSize="small" />,
              desc: 'Request a specific date off. The manager reviews and approves or denies the request.',
            },
            {
              type: 'Shift Swap',
              icon: <SwapHoriz fontSize="small" />,
              desc: 'Offer one of your assigned shifts for another employee to pick up. The shift opens for pickup once a manager approves (if approval is required).',
            },
          ].map(({ type, icon, desc }) => (
            <Box key={type} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
              <Box>
                <Typography variant="body2" fontWeight={700}>{type}</Typography>
                <Typography variant="body2" color="text.secondary">{desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <SectionHeading>Submitting a Time Off Request</SectionHeading>
        <StepList
          steps={[
            'Click "Request Off" in the Quick Action sidebar.',
            'Select the date you need off.',
            'Add an optional note for your manager.',
            'Click Submit.',
          ]}
        />
        <SectionHeading>Submitting a Swap Request</SectionHeading>
        <StepList
          steps={[
            'Click a shift card that you are assigned to.',
            'Select the "Swap" option from the shift detail view.',
            'Choose the shift you want to offer for swap.',
            'Add an optional message, then submit.',
          ]}
        />
        <SectionHeading>Managing Requests (Managers)</SectionHeading>
        <Para>
          Managers can view all pending requests by clicking <strong>Manage Requests</strong> in the
          Quick Action sidebar. Each request shows the employee name, request type, and date. Managers
          can approve or deny with a single click.
        </Para>
        <TipBox>
          If a schedule is configured to allow swaps or pickups without approval, they are processed
          automatically without manager intervention.
        </TipBox>
      </Box>
    ),
  },
  {
    id: 'team',
    title: 'Managing Your Team',
    icon: <Group fontSize="small" />,
    role: 'manager',
    searchText: 'invite staff employee email access level promote demote remove manage positions job role server cashier driver color add team roster manager',
    content: (
      <Box>
        <SectionHeading>Inviting Staff</SectionHeading>
        <Para>
          To add an employee to a schedule, send them an invitation link:
        </Para>
        <StepList
          steps={[
            'Click "Invite Staff" in the Quick Action sidebar.',
            'Enter the employee\'s email address.',
            'Optionally set their access level (manager or staff).',
            'Click Send Invite. The employee will receive a join link via email.',
          ]}
        />
        <SectionHeading>Managing Employees</SectionHeading>
        <Para>
          Click <strong>Manage Staff</strong> in the Quick Action sidebar to open the employee
          management panel. From here you can:
        </Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'View all employees on the schedule',
            'Change an employee\'s access level (promote to manager or demote to staff)',
            'Remove an employee from the schedule',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <SectionHeading>Managing Positions</SectionHeading>
        <Para>
          Positions help organize shifts by job type (e.g., "Server", "Cashier", "Driver"). To
          manage positions:
        </Para>
        <StepList
          steps={[
            'Click "Manage Positions" in the Quick Action sidebar.',
            'Use the + button to add a new position with a name and color.',
            'Click a position to edit or delete it.',
          ]}
        />
        <TipBox>
          Assigning positions to shifts helps staff quickly identify which shifts apply to their role.
        </TipBox>
      </Box>
    ),
  },
  {
    id: 'profile',
    title: 'Profile & Settings',
    icon: <Settings fontSize="small" />,
    role: 'all',
    searchText: 'profile settings account name email phone sms notifications dark mode password security change update photo contact',
    content: (
      <Box>
        <SectionHeading>Account Settings</SectionHeading>
        <Para>
          Access your profile settings by clicking the person icon in the top-right navigation bar.
          From here you can update:
        </Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'Display name and contact information',
            'Phone number for SMS notifications',
            'Account email address',
            'Profile photo',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <SectionHeading>Security</SectionHeading>
        <Para>
          The Security tab in Profile Settings lets you change your password and manage active
          sessions. We recommend using a strong, unique password.
        </Para>
        <SectionHeading>Notifications</SectionHeading>
        <Para>
          In the Notifications tab you can configure which events trigger alerts — for example,
          new shift assignments, approved requests, or upcoming schedule deadlines.
        </Para>
        <SectionHeading>Dark Mode</SectionHeading>
        <Para>
          Toggle between light and dark mode using the moon/sun icon in the top navigation bar.
          Your preference is saved automatically.
        </Para>
      </Box>
    ),
  },
  {
    id: 'billing',
    title: 'Billing & Subscription',
    icon: <Payment fontSize="small" />,
    role: 'manager',
    searchText: 'billing subscription plan payment method credit card invoice receipt cancel upgrade downgrade history tier employees schedules limit',
    content: (
      <Box>
        <SectionHeading>Subscription Plans</SectionHeading>
        <Para>
          Stafflows offers tiered subscription plans based on the number of employees and schedules
          your business needs. The plan you are on determines how many schedules and staff members
          you can have.
        </Para>
        <SectionHeading>Managing Your Plan</SectionHeading>
        <Para>
          Navigate to <strong>Profile Settings → Billing</strong> to view your current plan, upgrade
          or downgrade, and manage payment methods.
        </Para>
        <SectionHeading>Payment Methods</SectionHeading>
        <Para>
          You can add, update, or remove credit and debit cards from the Billing tab. All payment
          data is handled securely through our payment processor.
        </Para>
        <SectionHeading>Billing History</SectionHeading>
        <Para>
          View a full history of past invoices and download receipts from the Billing History section
          in the Billing tab.
        </Para>
        <SectionHeading>Cancelling Your Subscription</SectionHeading>
        <Para>
          To cancel, go to <strong>Profile Settings → Billing</strong> and click{' '}
          <strong>Cancel Subscription</strong>. Your access will remain active until the end of the
          current billing period.
        </Para>
        <TipBox>
          If you exceed your plan's limits (e.g., too many employees), you will be prompted to
          upgrade your plan before adding more.
        </TipBox>
      </Box>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: <Notifications fontSize="small" />,
    role: 'all',
    searchText: 'notifications bell unread sms text message phone alert assignment swap request deadline employee join in-app opt out consent',
    content: (
      <Box>
        <SectionHeading>In-App Notifications</SectionHeading>
        <Para>
          Click the bell icon in the top navigation bar to view your recent notifications. A red dot
          indicates you have unread notifications. Notifications are generated for events like:
        </Para>
        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
          {[
            'New shift assignments',
            'Shift swap requests and approvals',
            'Time-off request status changes',
            'Upcoming schedule submission deadlines',
            'New employee join requests',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <SectionHeading>SMS Notifications</SectionHeading>
        <Para>
          Stafflows can send text message alerts for important events. To enable SMS notifications,
          add a verified phone number in <strong>Profile Settings → Profile</strong>, then configure
          which events trigger SMS in the Notifications tab.
        </Para>
        <TipBox>
          You'll be asked to consent to SMS messaging when you first add your phone number. You can
          opt out at any time from the Notifications settings tab.
        </TipBox>
      </Box>
    ),
  },
];

const HelpWikiModal: React.FC<HelpWikiModalProps> = ({ open, onClose, initialSection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedSection, setSelectedSection] = useState(initialSection ?? 'overview');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && initialSection) {
      setSelectedSection(initialSection);
    }
  }, [open, initialSection]);

  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return WIKI_SECTIONS;
    return WIKI_SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.searchText.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  // When search results change, auto-select the first result
  const displaySection = isSearching
    ? (filteredSections.find((s) => s.id === selectedSection) ?? filteredSections[0])
    : WIKI_SECTIONS.find((s) => s.id === selectedSection) ?? WIKI_SECTIONS[0];

  const handleSectionClick = (id: string) => {
    setSelectedSection(id);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    // Auto-select first result as user types
    if (val.trim()) {
      const q = val.trim().toLowerCase();
      const first = WIKI_SECTIONS.find(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.searchText.toLowerCase().includes(q)
      );
      if (first) setSelectedSection(first.id);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100%' : '80vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline color="primary" />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
            Stafflows Help Center
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Search help articles…"
          value={searchQuery}
          onChange={handleSearchChange}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flexGrow: 1 }}>
        {/* Sidebar nav (desktop) */}
        {!isMobile && (
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              py: 1,
            }}
          >
            {filteredSections.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <SearchOff sx={{ color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="caption" color="text.disabled">
                  No results
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filteredSections.map((section) => (
                  <React.Fragment key={section.id}>
                    <ListItemButton
                      selected={displaySection?.id === section.id}
                      onClick={() => handleSectionClick(section.id)}
                      sx={{
                        borderRadius: 0,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '& .MuiListItemText-primary': { color: 'primary.contrastText' },
                          '& .MuiSvgIcon-root': { color: 'primary.contrastText' },
                          '&:hover': { bgcolor: 'primary.dark' },
                        },
                      }}
                    >
                      <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>{section.icon}</Box>
                      <ListItemText
                        primary={section.title}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      />
                      {section.role && section.role !== 'all' && (
                        <Chip
                          label={section.role}
                          size="small"
                          color={section.role === 'manager' ? 'warning' : 'info'}
                          sx={{ fontSize: '0.6rem', height: 18 }}
                        />
                      )}
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Main content */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
          {/* Mobile: accordion layout */}
          {isMobile ? (
            filteredSections.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <SearchOff sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" color="text.disabled" fontWeight={600}>
                  No results for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Try a different keyword.
                </Typography>
              </Box>
            ) : (
              filteredSections.map((section) => (
                <Accordion
                  key={section.id}
                  defaultExpanded={isSearching || section.id === 'overview'}
                  disableGutters
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {section.icon}
                      <Typography variant="body2" fontWeight={700}>{section.title}</Typography>
                      {section.role && section.role !== 'all' && (
                        <Chip
                          label={section.role}
                          size="small"
                          color={section.role === 'manager' ? 'warning' : 'info'}
                          sx={{ fontSize: '0.6rem', height: 18 }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>{section.content}</AccordionDetails>
                </Accordion>
              ))
            )
          ) : (
            /* Desktop: single panel or empty state */
            filteredSections.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <SearchOff sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.disabled" fontWeight={600}>
                  No results for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Try searching for a feature, action, or keyword.
                </Typography>
              </Box>
            ) : (
              <Box>
                {isSearching && (
                  <Paper
                    variant="outlined"
                    sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover' }}
                  >
                    <SearchIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {filteredSections.length} result{filteredSections.length !== 1 ? 's' : ''} for{' '}
                      <strong>"{searchQuery}"</strong>
                    </Typography>
                  </Paper>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>{displaySection?.icon}</Box>
                  <Typography variant="h5" fontWeight={800}>
                    {displaySection?.title}
                  </Typography>
                  {displaySection?.role && displaySection.role !== 'all' && (
                    <Chip
                      label={`${displaySection.role} only`}
                      size="small"
                      color={displaySection.role === 'manager' ? 'warning' : 'info'}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {displaySection?.content}
              </Box>
            )
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HelpWikiModal;
